import React, { Fragment, useMemo } from "react";
import { useSelector } from "react-redux";

import CheckoutCard from "app/checkout/CheckoutCard";
import styles from "./DeliveryDetailsBlock.module.scss";
import {
  addressLinesFromAddress,
  getSelectedShippingAddress,
} from "shared/utility";

/**
 * "Standard delivery (FREE)". The shipping method carries a name and a price
 * and nothing else — there is no delivery-estimate field behind the
 * "3-5 business days" line in the design, so it isn't rendered.
 */
const shippingMethodLabel = (shippingMethod) => {
  if (!shippingMethod?.name) {
    return null;
  }

  const price = shippingMethod.price;

  if (!price) {
    return shippingMethod.name;
  }

  return `${shippingMethod.name} (${
    price.amount === "0.00" ? "FREE" : price.formatted
  })`;
};

const AddressLines = ({ lines }) => (
  <p className="gkit-p-15 gkit-mobile-13 gkit-m-0">
    {lines.map((line, idx) => (
      <Fragment key={String(idx)}>
        {idx > 0 && <br />}
        {line}
      </Fragment>
    ))}
  </p>
);

const DeliveryDetailsBlock = ({ className, shippingMethod }) => {
  const { shippingAddress, availableShippingAddresses, completedCheckout } =
    useSelector((s) => s.checkout);

  const selectedShippingAddress = useMemo(
    () =>
      getSelectedShippingAddress({
        shippingAddress,
        availableShippingAddresses,
        completedCheckout,
      }),
    [shippingAddress, availableShippingAddresses, completedCheckout],
  );

  const methodLabel = shippingMethodLabel(shippingMethod);

  // A non-shipped order has neither, so the whole card drops out rather than
  // leaving an empty one behind.
  if (!selectedShippingAddress && !methodLabel) {
    return null;
  }

  return (
    <CheckoutCard className={className}>
      <div className={styles.container}>
        {selectedShippingAddress && (
          <div className={styles.column}>
            <p className="gkit-p-17 gkit-mobile-15 gkit-bold gkit-mt-0 gkit-mb-2">
              Delivering to
            </p>
            <AddressLines
              lines={addressLinesFromAddress(selectedShippingAddress)}
            />
          </div>
        )}

        {methodLabel && (
          <div className={styles.column}>
            <p className="gkit-p-17 gkit-mobile-15 gkit-bold gkit-mt-0 gkit-mb-2">
              Shipping method
            </p>
            <p className="gkit-p-15 gkit-mobile-13 gkit-m-0">{methodLabel}</p>
          </div>
        )}
      </div>
    </CheckoutCard>
  );
};

export default DeliveryDetailsBlock;
